/**
 * The hotspot map (pillar B): "where is the product weakest". Ranks
 * areas by pressure for one grouping key, with the queue's filters,
 * a window, coverage per key, and a drill-down panel. Findings page,
 * `?tab=hotspots`.
 */
import {
  type FindingQueueQuery,
  formatHotspotWindow,
  HOTSPOT_KEYS,
  type HotspotBy,
  type HotspotList,
  parseHotspotWindow,
} from "@iamu/shared";
import { AlertCircle, RotateCcw, Route } from "lucide-react";
import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { EmptyState } from "@/components/empty-state";
import { hasBuildScope } from "@/components/findings-scope/scope-url-state";
import { QueueFilters } from "@/components/triage/queue-filters";
import {
  parseQueueUrl,
  queueUrlParams,
} from "@/components/triage/queue-url-state";
import { Button } from "@/components/ui/button";
import { FilterBar, FilterPill } from "@/components/ui/filter-bar";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { STALE_TIMES } from "@/lib/query-performance";
import { trpc } from "@/lib/trpc";
import { useMediaQuery } from "@/lib/use-media-query";
import { HotspotMatrix } from "./hotspot-matrix";
import { HotspotPanel } from "./hotspot-panel";
import { HotspotCards, HotspotTable } from "./hotspot-table";
import {
  HOTSPOT_PARAMS,
  parseHotspotUrl,
  withHotspotState,
} from "./hotspot-url-state";
import { coveragePercent, KEY_LABEL, KEY_NOUN } from "./hotspot-vocabulary";
import { PressureFormulaPopover } from "./pressure-formula";
import { CoverageNote, NoRuns } from "./hotspots-coverage-note";

/**
 * Runs per scenario the map ranks over — inside the page scope's period
 * (the period itself lives in the scope bar above the tabs).
 */
const runWindows = (inPeriod: boolean) =>
  [
    { value: "last5", label: "Latest 5 runs per scenario" },
    { value: "last10", label: "Latest 10 runs per scenario" },
    { value: "all", label: inPeriod ? "All runs in period" : "All runs" },
  ] as const;

export function HotspotsView({ projectId }: { projectId: string }) {
  const [params, setParams] = useSearchParams();
  const url = useMemo(() => parseHotspotUrl(params), [params]);
  const isLg = useMediaQuery("(min-width: 1024px)");
  const [focusKey, setFocusKey] = useState<string | null>(null);

  const { data, isLoading, error, refetch } = trpc.hotspot.list.useQuery(
    {
      projectId,
      by: url.by,
      window: url.window,
      filters: url.filters,
      includeSecondary: url.includeSecondary,
    },
    { staleTime: STALE_TIMES.list },
  );

  const patch = (p: Parameters<typeof withHotspotState>[1]) =>
    setParams(withHotspotState(params, p), { replace: true });
  const hrefFor = (key: string) => `?${withHotspotState(params, { key })}`;
  const pivotHref = (by: HotspotBy, key: string) =>
    `?${withHotspotState(params, { by, key })}`;

  // The queue's filter bar writes queue params; keep the map's own.
  const onFilters = (next: FindingQueueQuery) => {
    const p = queueUrlParams({
      ...parseQueueUrl(params),
      query: next,
      focusId: null,
    });
    p.delete("view");
    for (const [k, v] of params) {
      if (k === "view" || HOTSPOT_PARAMS.includes(k)) p.set(k, v);
    }
    setParams(p, { replace: true });
  };

  const showMatrix = isLg && url.by === "capability" && url.layout === "matrix";
  const focused =
    data?.rows.find((r) => r.key === (focusKey ?? url.key)) ??
    data?.rows[0] ??
    null;

  return (
    <div className="space-y-5">
      <div className="space-y-3">
        <FilterBar>
          <FilterPill
            ariaLabel="Group hotspots by"
            value={url.by}
            onValueChange={(by) => patch({ by, key: null })}
            options={HOTSPOT_KEYS.map((k) => ({
              value: k,
              label: data
                ? `${KEY_LABEL[k]} · ${coveragePercent(data.coverage[k])}%`
                : KEY_LABEL[k],
            }))}
          />
          <FilterPill
            ariaLabel="Runs"
            value={formatHotspotWindow(url.window)}
            onValueChange={(w) => patch({ window: parseHotspotWindow(w) })}
            options={runWindows(Boolean(url.filters.period))}
          />
          {url.by === "capability" && (
            <label className="inline-flex h-9 items-center gap-2 px-1 text-sm text-foreground">
              <Switch
                checked={url.includeSecondary}
                onCheckedChange={(v) => patch({ includeSecondary: v })}
              />
              Count secondary tags
            </label>
          )}
          {isLg && url.by === "capability" && (
            <fieldset className="inline-flex items-center gap-1">
              <legend className="sr-only">Layout</legend>
              {(["list", "matrix"] as const).map((layout) => (
                <Button
                  key={layout}
                  variant={url.layout === layout ? "secondary" : "ghost"}
                  size="sm"
                  aria-pressed={url.layout === layout}
                  onClick={() => patch({ layout })}
                >
                  {layout === "list" ? "List" : "By persona"}
                </Button>
              ))}
            </fieldset>
          )}
        </FilterBar>
        <QueueFilters
          projectId={projectId}
          query={parseQueueUrl(params).query}
          onChange={onFilters}
          variant="hotspots"
        />
      </div>

      {isLoading ? (
        <Skeleton className="h-64 w-full" />
      ) : error || !data ? (
        <EmptyState
          icon={AlertCircle}
          title="Failed to load hotspots"
          description="We couldn't calculate hotspots for this project. Please try again."
          actions={
            <Button
              onClick={() => refetch()}
              variant="outline"
              className="gap-2"
            >
              <RotateCcw className="h-4 w-4" />
              Try again
            </Button>
          }
        />
      ) : data.totals.runs === 0 ? (
        <NoRuns
          filtered={Object.keys(url.filters).length > 0}
          window={url.window.kind !== "lastRuns"}
          scoped={Boolean(url.filters.period) || hasBuildScope(url.filters)}
        />
      ) : (
        <>
          <CoverageNote projectId={projectId} data={data} by={url.by} />
          {showMatrix ? (
            <HotspotMatrix
              projectId={projectId}
              window={url.window}
              filters={url.filters}
              includeSecondary={url.includeSecondary}
              hrefFor={hrefFor}
            />
          ) : data.rows.length === 0 ? (
            <EmptyState
              icon={Route}
              title={
                url.by === "route"
                  ? "No page routes recorded"
                  : `No ${KEY_NOUN[url.by]} to rank`
              }
              description={
                url.by === "route"
                  ? "Step URLs are grouped into pages using route patterns configured in project settings."
                  : "Nothing in this window carries this key."
              }
              actions={
                url.by === "route" ? (
                  <Button variant="outline" asChild>
                    <Link
                      to={`/projects/${projectId}/settings?tab=route-patterns`}
                    >
                      Configure route patterns
                    </Link>
                  </Button>
                ) : undefined
              }
              variant="compact"
            />
          ) : isLg ? (
            <HotspotTable
              by={url.by}
              rows={data.rows}
              sortCol={url.sortCol}
              sortDir={url.sortDir}
              onSort={(col) => {
                // A new column starts A to Z for names, highest first
                // for numbers; the same column again flips it.
                const first = col === "area" ? "ascending" : "descending";
                patch({
                  sortCol: col,
                  sortDir:
                    url.sortCol !== col
                      ? first
                      : url.sortDir === "descending"
                        ? "ascending"
                        : "descending",
                });
              }}
              hrefFor={hrefFor}
              onFocusRow={setFocusKey}
            />
          ) : (
            <HotspotCards by={url.by} rows={data.rows} hrefFor={hrefFor} />
          )}
          <footer className="flex flex-wrap items-center justify-between gap-2 border-t pt-3 text-sm text-muted-foreground">
            <p>
              Pressure reflects unresolved problem severity normalized by steps
              evaluated.
            </p>
            <PressureFormulaPopover row={focused} prior={data.prior} />
          </footer>
        </>
      )}

      {url.key && (
        <HotspotPanel
          projectId={projectId}
          by={url.by}
          hotspotKey={url.key}
          window={url.window}
          filters={url.filters}
          includeSecondary={url.includeSecondary}
          pivotHref={pivotHref}
          onClose={() => patch({ key: null })}
        />
      )}
    </div>
  );
}
